/**
 * Stock Price hooks — directApiClient-backed
 */

'use client';

import { useQuery } from '@tanstack/react-query';
import { directApiClient } from '@/lib/api/direct-client';
import type { StockPriceRequest, StockPriceResponse } from './types';

interface UseStockPriceParams {
  ticker: string | undefined;
  dateOfDeath?: string;
  numberOfShares?: number;
  exchange?: 'AU' | 'US' | 'LSE';
  enabled?: boolean;
}

interface UseStockPriceReturn {
  data: StockPriceResponse | undefined;
  isLoading: boolean;
  error: string | null;
  /**
   * The one thing the user needs to be told, or `null` when the lookup was
   * unremarkable — a failed request, a delisted security, or a date of death with
   * no market price.
   *
   * This exists because `error` alone was never enough. Every consumer
   * destructured only `data` and `isLoading`, so a lookup that failed showed the
   * solicitor nothing at all: the spinner stopped, the price fields stayed blank,
   * and they went and found the price by hand (VALE-763). Folding every
   * "explain this to a human" case into a single field makes it hard to drop.
   */
  notice: string | null;
  /**
   * True when Vale cannot price this holding automatically — the security is
   * delisted, or the date of death has no market price. A DURABLE condition, not
   * a transient fetch failure: surfaces that promise automatic valuation must
   * stop promising it, or they contradict the notice sitting right above them.
   */
  automaticPricingUnavailable: boolean;
  refetch: () => void;
}

async function fetchStockPrice(
  params: StockPriceRequest
): Promise<StockPriceResponse> {
  const queryParams: Record<string, string> = {
    ticker: params.ticker.toUpperCase()
  };
  if (params.dateOfDeath) queryParams.dateOfDeath = params.dateOfDeath;
  if (params.numberOfShares !== undefined && params.numberOfShares > 0) {
    queryParams.numberOfShares = String(params.numberOfShares);
  }
  if (params.exchange) queryParams.exchange = params.exchange;

  return directApiClient.get<StockPriceResponse>('/stocks/prices', {
    params: queryParams
  });
}

/** Build the user-facing notice, most specific cause first. */
/**
 * A failure with nothing in it for the reader — the shapes axios and `fetch`
 * produce when the request never reached an answer, as opposed to an error the
 * API deliberately worded. Anchored, so a backend message that happens to
 * contain one of these phrases still passes through intact.
 */
const TRANSPORT_FAILURE =
  /^(network error|request failed with status code \d+|timeout of \d+ms exceeded|canceled|aborted|fetch failed)/i;

function deriveNotice(
  data: StockPriceResponse | undefined,
  error: Error | null
): string | null {
  if (error) {
    /*
      **The backend's own message when it wrote one, ours when it did not**
      (VALE-1008).

      This surfaces on a money row in a legal record, so it has to be something a
      solicitor can act on. Most of the time it already is: the API names the
      security and what went wrong (`Ticker N/A was not found on the AU
      exchange.`), and passing that through is the whole reason this branch
      exists (VALE-763).

      What must not reach the row is a bare transport failure. Those carry no
      information the reader can use, are not their fault, and are exactly what a
      workstation without an `EODHD_API_TOKEN` produces on every lookup. Say the
      two things they can act on instead: the figure is not coming automatically,
      and entering it by hand is the way forward. The real error is still on the
      console for whoever is debugging it.
    */
    return TRANSPORT_FAILURE.test(error.message.trim())
      ? 'Vale could not reach the share price service, so this holding has no automatic value right now. Enter the value by hand, or try again later.'
      : error.message;
  }
  if (!data) {
    return null;
  }
  // The backend already phrases this for a solicitor, naming the security, the
  // date it stopped trading, and that the value needs entering by hand.
  if (data.dateOfDeathPriceUnavailable) {
    return data.dateOfDeathPriceUnavailable.message;
  }
  if (data.listingStatus === 'delisted') {
    const name = data.companyName ?? data.ticker;
    const lastTrade =
      data.lastTradedDate && data.lastTradedPrice !== undefined
        ? ` It last traded on ${data.lastTradedDate} at $${data.lastTradedPrice.toFixed(2)}.`
        : '';
    return `${name} is no longer listed, so it has no current market price.${lastTrade}`;
  }
  return null;
}

export function useStockPrice({
  ticker,
  dateOfDeath,
  numberOfShares,
  exchange,
  enabled = true
}: UseStockPriceParams): UseStockPriceReturn {
  const isEnabled = enabled && !!ticker && ticker.trim().length > 0;

  const query = useQuery<StockPriceResponse, Error>({
    queryKey: ['stock-price', ticker, dateOfDeath, numberOfShares, exchange],
    queryFn: () =>
      fetchStockPrice({
        ticker: ticker as string,
        dateOfDeath,
        numberOfShares,
        exchange
      }),
    enabled: isEnabled,
    staleTime: 60 * 1000
  });

  // Values are authoritative from the backend: `/stocks/prices` already returns
  // `currentValue` / `valueAtDateOfDeath` (price × numberOfShares, computed
  // server-side when units are supplied — see StockPriceResponse). We pass them
  // straight through rather than recomputing on the client (LF-4069).
  return {
    data: query.data,
    isLoading: isEnabled && query.isLoading,
    error: query.error ? query.error.message : null,
    notice: deriveNotice(query.data, query.error),
    automaticPricingUnavailable:
      query.data?.listingStatus === 'delisted' ||
      !!query.data?.dateOfDeathPriceUnavailable,
    refetch: () => {
      void query.refetch();
    }
  };
}

export type { StockPriceResponse, StockPriceRequest };
